import { useCallback, useState } from "react";

type Options = {
    onConfirm?: () => Promise<void> | void;
    permissionError?: string;
    genericError?: string;
};

export const useConfirmDeleteModal = (options: Options = {}) => {
    const {
        onConfirm,
        permissionError = "You don't have permission to delete this note.",
        genericError = "Failed to delete note. Please try again.",
    } = options;
    const [open, setOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    const openModal = useCallback(() => {
        setDeleteError(null);
        setOpen(true);
    }, []);

    const closeModal = useCallback(() => {
        if (!isDeleting) {
            setOpen(false);
            setDeleteError(null);
        }
    }, [isDeleting]);

    const handleConfirm = useCallback(async () => {
        if (isDeleting) return;
        setIsDeleting(true);
        setDeleteError(null);

        try {
            await onConfirm?.();
            setOpen(false);
        } catch (err: any) {
            const status = err?.response?.status;
            if (status === 403) {
                setDeleteError(permissionError);
            } else {
                setDeleteError(genericError);
            }
        } finally {
            setIsDeleting(false);
        }
    }, [onConfirm, isDeleting]);

    const handleCancel = useCallback(() => {
        if (!isDeleting) {
            setOpen(false);
            setDeleteError(null);
        }
    }, [isDeleting]);

    return {
        open,
        isDeleting,
        deleteError,
        openModal,
        closeModal,
        handleConfirm,
        handleCancel,
    };
};
